import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronLeft, Plus, Minus, ShoppingBag, Check, Star, Sparkles } from "lucide-react";
import { api, formatRON, bestQtyTier, nextQtyTier } from "../lib/shop";
import { useT, localizedProduct } from "../lib/i18n";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import SEO from "../components/SEO";

export default function ProductDetail() {
  const { slug } = useParams();
  const { t, lang } = useT();
  const [raw, setRaw] = useState(null);
  const [active, setActive] = useState(0);
  const [qty, setQty] = useState(1);
  const { add } = useCart();

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => { setRaw(r.data); setActive(0); }).catch(() => setRaw(false));
  }, [slug]);

  if (raw === false) return <div className="max-w-7xl mx-auto px-6 py-32 text-center">{t("product.notfound")}</div>;
  if (!raw) return <div className="max-w-7xl mx-auto px-6 py-32 text-gray-400 uppercase tracking-widest text-sm">{t("product.loading")}</div>;

  const p = localizedProduct(raw, lang);
  const discount = p.old_price ? Math.round((1 - p.price / p.old_price) * 100) : null;

  const productJsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: p.name,
    description: p.description,
    image: p.images,
    brand: { "@type": "Brand", name: "izaana tools" },
    offers: {
      "@type": "Offer",
      url: typeof window !== "undefined" ? window.location.href : "",
      priceCurrency: "RON",
      price: p.price,
      availability: "https://schema.org/InStock",
      itemCondition: "https://schema.org/NewCondition",
    },
    ...(p.rating ? {
      aggregateRating: { "@type": "AggregateRating", ratingValue: p.rating, reviewCount: p.reviews || 1 },
    } : {}),
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16" data-testid="product-detail">
      <SEO
        title={p.short_name}
        description={(p.description || "").slice(0, 160)}
        image={p.images[0]}
        path={`/produs/${p.slug}`}
        jsonLd={productJsonLd}
      />
      <Link to={`/categorie/${p.category}`} className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-gray-500 hover:text-gray-900 mb-8">
        <ChevronLeft size={14} /> {p.category === 'cutite' ? t("nav.knives") : t("nav.tools")}
      </Link>

      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-7">
          <div className="aspect-square border border-gray-200 bg-gradient-to-br from-gray-50 to-white overflow-hidden relative">
            <img src={p.images[active]} alt={p.name} className="w-full h-full object-contain p-10" />
            {discount && (
              <span className="absolute top-4 left-4 bg-gray-900 text-white text-xs font-bold px-3 py-1.5 font-mono">−{discount}%</span>
            )}
          </div>
          <div className="grid grid-cols-5 gap-3 mt-4">
            {p.images.map((src, i) => (
              <button key={i} onClick={() => setActive(i)} className={`aspect-square border bg-white p-2 transition ${active === i ? 'border-gray-900' : 'border-gray-200 hover:border-gray-400'}`} data-testid={`thumb-${i}`}>
                <img src={src} alt="" className="w-full h-full object-contain" />
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5">
          <p className="text-xs uppercase tracking-[0.3em] text-gray-400">{p.category === 'cutite' ? t("nav.knives.long") : t("nav.tools.long")}</p>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter mt-3 leading-tight">{p.name}</h1>

          {p.rating ? (
            <div className="flex items-center gap-2 mt-4">
              <div className="flex">
                {[1,2,3,4,5].map((s) => (
                  <Star key={s} size={16} fill={s <= Math.round(p.rating) ? "#111827" : "transparent"} stroke="#111827" strokeWidth={1.5} />
                ))}
              </div>
              <span className="text-sm font-mono text-gray-600">{p.rating.toFixed(2)} · {p.reviews} {t("product.reviews")}</span>
            </div>
          ) : null}

          <div className="mt-6 flex items-baseline gap-3">
            <span className="font-display text-4xl font-black font-mono text-gray-900" data-testid="product-price">{formatRON(p.price)}</span>
            {p.old_price && <span className="text-gray-400 line-through text-lg font-mono">{formatRON(p.old_price)}</span>}
          </div>

          <p className="mt-8 text-gray-700 leading-relaxed">{p.description}</p>

          {p.description_html && (
            <div
              className="mt-6 prose-anaiza text-sm leading-relaxed text-gray-700"
              dangerouslySetInnerHTML={{ __html: p.description_html }}
              data-testid="product-description-html"
            />
          )}

          <ul className="mt-6 space-y-2">
            {(p.highlights || []).map((h, i) => (
              <li key={i} className="flex items-start gap-3 text-sm">
                <Check size={16} strokeWidth={2} className="text-gray-900 mt-0.5 flex-shrink-0" /> {h}
              </li>
            ))}
          </ul>

          {p.quantity_discount_tiers && p.quantity_discount_tiers.length > 0 && (() => {
            const tier = bestQtyTier(p.quantity_discount_tiers, qty);
            const nextT = nextQtyTier(p.quantity_discount_tiers, qty);
            return (
              <div className="mt-8 border border-gray-900 bg-gray-50/70 p-5" data-testid="product-tier-panel">
                <div className="flex items-center gap-2">
                  <Sparkles size={16} strokeWidth={1.5} className="text-[#1ebe57]" />
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-gray-900">{t("product.qty.discount")}</p>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  {p.quantity_discount_tiers.map((tr) => {
                    const reached = qty >= tr.qty;
                    return (
                      <div key={tr.qty} className={`px-4 py-3 border ${reached ? 'border-[#1ebe57] bg-[#1ebe57]/10' : 'border-gray-300 bg-white'}`}>
                        <p className={`font-display text-xl font-black ${reached ? 'text-[#188f42]' : 'text-gray-900'}`}>{tr.qty}+ {t("product.qty.sets")}</p>
                        <p className={`text-sm font-bold ${reached ? 'text-[#188f42]' : 'text-gray-500'}`}>−{tr.percent}% {t("product.qty.discount.sub")}</p>
                      </div>
                    );
                  })}
                </div>
                {tier.percent > 0 ? (
                  <p className="mt-4 text-xs text-[#188f42] font-semibold flex items-center gap-2" data-testid="tier-applied">
                    <Check size={14} strokeWidth={2} /> −{tier.percent}% {t("product.qty.applied")} {qty} {t("product.qty.sets")} · {t("product.qty.save")} {formatRON((p.price * qty * tier.percent) / 100)}
                  </p>
                ) : nextT ? (
                  <p className="mt-4 text-xs text-gray-600">
                    {t("product.qty.addmore")} <span className="font-bold text-gray-900">{nextT.qty - qty} {nextT.qty - qty === 1 ? t("product.qty.set") : t("product.qty.sets")}</span> {t("product.qty.toget")} <span className="font-bold text-[#1ebe57]">−{nextT.percent}%</span>
                  </p>
                ) : null}
              </div>
            );
          })()}

          <div className="mt-10 flex items-stretch gap-3">
            <div className="inline-flex items-center border border-gray-300">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-3 hover:bg-gray-100" data-testid="qty-minus"><Minus size={16} /></button>
              <span className="px-5 font-semibold" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="px-3 py-3 hover:bg-gray-100" data-testid="qty-plus"><Plus size={16} /></button>
            </div>
            <button
              onClick={() => { add(p, qty); toast.success(t("product.added")); }}
              className="flex-1 flex items-center justify-center gap-2 bg-gray-900 text-white px-6 py-4 font-semibold uppercase tracking-wider text-sm hover:bg-gray-800 transition"
              data-testid="add-to-cart-button"
            >
              <ShoppingBag size={18} strokeWidth={1.5} /> {t("product.add")}
            </button>
          </div>

          <div className="mt-12 border border-gray-200">
            <div className="border-b border-gray-200 px-5 py-3 bg-gray-50">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-gray-700">{t("product.specs")}</span>
            </div>
            <dl className="divide-y divide-gray-200">
              {Object.entries(p.specs || {}).map(([k, v]) => (
                <div key={k} className="grid grid-cols-2 px-5 py-3 text-sm">
                  <dt className="text-gray-500">{k}</dt>
                  <dd className="text-gray-900 font-medium">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}
